import {Component, OnDestroy, OnInit} from '@angular/core';
import {AdminService} from '../../services/admin.service';
import {interval, Subscription} from 'rxjs';
import {startWith} from 'rxjs/operators';
import {DatabaseStatsDto, OperationStatsDto, ServerStatusDto} from './dtos';
import {ChartConfiguration, ChartOptions, ChartType} from 'chart.js';

@Component({
  selector: 'app-mongo',
  templateUrl: './mongo.component.html'
})
export class MongoComponent implements OnInit, OnDestroy {
  private readonly POLLING_INTERVAL = 30000;
  private readonly MAX_HISTORY_POINTS = 120;
  private readonly CONNECTION_WARNING_THRESHOLD = 0.8;
  private polling$: Subscription;

  serverInfo: ServerStatusDto;
  latestDatabaseStats: DatabaseStatsDto;

  chartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'minute',
          displayFormats: {
            minute: 'HH:mm'
          }
        }
      },
      y: {
        beginAtZero: false,
        ticks: {
          callback: (value) => Math.round(value as number)
        }
      }
    }
  };

  connectionsLabels: string[] = [];
  connectionsData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Current Connections',
        borderColor: '#2196f3',
        backgroundColor: 'rgba(33,150,243,0.1)',
        fill: true
      }
    ]
  };

  // Operations Chart
  operationsLabels: string[] = [];
  operationsData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Active Operations',
        borderColor: '#4caf50',
        backgroundColor: 'rgba(76,175,80,0.1)',
        fill: true
      }
    ]
  };

  chartType: ChartType = 'line';
  operationStatsHistory: Array<{ timestamp: Date; data: OperationStatsDto }> = [];

  get connectionWarning(): boolean {
    if (!this.serverInfo) {
      return false;
    }
    const {currentConnections, availableConnections} = this.serverInfo;
    return currentConnections / (currentConnections + availableConnections) >= this.CONNECTION_WARNING_THRESHOLD;
  }

  get connectionPercentage(): number {
    if (!this.serverInfo) {
      return 0;
    }
    const {currentConnections, availableConnections} = this.serverInfo;
    return (currentConnections / (currentConnections + availableConnections)) * 100;
  }

  constructor(private adminService: AdminService) {
  }

  ngOnInit() {
    this.startPolling();
  }

  ngOnDestroy() {
    if (this.polling$) {
      this.polling$.unsubscribe();
    }
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private updateChartScales(data: number[]) {
    if (!data || data.length === 0) {
      return;
    }

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min;
    const padding = range * 0.2;

    if (this.chartOptions.scales?.y) {
      this.chartOptions.scales.y.min = Math.max(0, min - padding);
      this.chartOptions.scales.y.max = max + padding;
    }
  }

  private updateConnectionsChart() {
    if (this.serverInfo) {
      const timestamp = new Date().toISOString();
      const {currentConnections} = this.serverInfo;

      this.connectionsData.labels!.push(timestamp);
      this.connectionsData.datasets[0].data.push(currentConnections);

      if (this.connectionsData.labels!.length > this.MAX_HISTORY_POINTS) {
        this.connectionsData.labels!.shift();
        this.connectionsData.datasets[0].data.shift();
      }

      this.updateChartScales(this.connectionsData.datasets[0].data as number[]);
    }
  }

  private updateCharts() {
    this.updateConnectionsChart();
    this.updateOperationsChart();
  }

  private updateOperationsChart() {
    if (this.operationStatsHistory.length > 0) {
      this.operationsData.labels = this.operationStatsHistory.map(item => item.timestamp.toISOString());
      const data = this.operationStatsHistory.map(item => item.data.activeOperations);
      this.operationsData.datasets[0].data = data;

      this.updateChartScales(data);
    }
  }

  private loadAllStats() {
    this.adminService.getMongoServer().subscribe(data => {
      this.serverInfo = data;
      this.updateCharts();
    });

    this.adminService.getMongoStats().subscribe(data => {
      this.latestDatabaseStats = data;
    });

    this.adminService.getMongoOperationStats().subscribe(data => {
      this.addToHistory(data);
    });
  }

  private addToHistory(newData: OperationStatsDto) {
    this.operationStatsHistory.push({
      timestamp: new Date(),
      data: newData
    });

    if (this.operationStatsHistory.length > this.MAX_HISTORY_POINTS) {
      this.operationStatsHistory.shift();
    }
    this.updateCharts();
  }

  private startPolling() {
    this.polling$ = interval(this.POLLING_INTERVAL)
    .pipe(startWith(0))
    .subscribe(() => this.loadAllStats());
  }
}
